import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import StatusBadge from '@/components/StatusBadge';
import CEOInsightsPanel from '@/components/CEOInsightsPanel';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList, Legend
} from 'recharts';
import { Clock, FileText, AlertTriangle, CheckCircle, TrendingUp, X, Briefcase, Users, Calendar, Coffee, Sigma, Bell, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ROLE_LABELS = {
  Faculty: 'Faculty',
  CIC: 'CIC',
  AcademicHead: 'Head',
  PrincipalSSV: 'Principal',
  SuperAdmin: 'Super Admin',
  CEO: 'CEO'
};

const BAR_COLORS = ['#1D4ED8', '#0F172A', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#14532D', '#7C3AED'];

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [allReports, setAllReports] = useState([]);
  const [facultyComparison, setFacultyComparison] = useState([]);
  const [cicComparison, setCicComparison] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCard, setActiveCard] = useState(null);
  const [roleFilter, setRoleFilter] = useState('All');
  const [centreFilter, setCentreFilter] = useState('All');
  const [centres, setCentres] = useState([]);
  const [weeklyAlerts, setWeeklyAlerts] = useState([]);
  // Date range filter (default: current calendar month start → today)
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const todayStr = today.toISOString().split('T')[0];
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(todayStr);
  // Sort state for Staff Comparison table
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  // Centre Staff Status (Task 2)
  const [centreStaffStatus, setCentreStaffStatus] = useState(null);
  const [showStaffStatus, setShowStaffStatus] = useState(false);
  // Clarification SLA (Task 3)
  const [clarAlerts, setClarAlerts] = useState({ responder_items: [], requester_items: [] });

  const isAdmin = ['CIC', 'AcademicHead', 'PrincipalSSV', 'SuperAdmin', 'CEO'].includes(user?.role);
  const isAHOrAbove = ['AcademicHead', 'PrincipalSSV', 'SuperAdmin', 'CEO'].includes(user?.role);
  const isSuperOrPrincipal = ['PrincipalSSV', 'SuperAdmin', 'CEO'].includes(user?.role);
  const showCentreFilter = ['AcademicHead', 'PrincipalSSV', 'SuperAdmin', 'CEO'].includes(user?.role);
  const canCreateReport = ['Faculty', 'CIC', 'AcademicHead', 'PrincipalSSV'].includes(user?.role) || user?.also_faculty;

  useEffect(() => {
    if (showCentreFilter) {
      api.getCentres().then(({ data }) => setCentres(data)).catch(() => {});
    }
  }, [showCentreFilter]);

  // Weekly-summary deadline alerts (CIC: Thu; Head: Fri; Principal: Sat)
  useEffect(() => {
    if (['CIC', 'AcademicHead', 'PrincipalSSV'].includes(user?.role)) {
      api.getWeeklySummaryAlerts()
        .then(({ data }) => setWeeklyAlerts(data?.alerts || []))
        .catch(() => {});
    }
  }, [user?.role]);

  // Centre Staff Status (Task 2) — only admin roles; Faculty does NOT see this card.
  useEffect(() => {
    if (['CIC', 'AcademicHead', 'PrincipalSSV', 'SuperAdmin', 'CEO'].includes(user?.role)) {
      const c = showCentreFilter ? (centreFilter && centreFilter !== 'All' ? centreFilter : null) : null;
      api.getCentreStaffStatus(c, fromDate, toDate)
        .then(({ data }) => setCentreStaffStatus(data))
        .catch(() => setCentreStaffStatus(null));
    }
  }, [user?.role, centreFilter, fromDate, toDate, showCentreFilter]);

  // Clarification SLA overdue (Task 3)
  useEffect(() => {
    if (['CIC', 'AcademicHead', 'PrincipalSSV', 'CEO', 'SuperAdmin'].includes(user?.role)) {
      api.getOverdueClarifications()
        .then(({ data }) => setClarAlerts(data || { responder_items: [], requester_items: [] }))
        .catch(() => {});
    }
  }, [user?.role]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const c = showCentreFilter ? centreFilter : null;
        const promises = [
          api.getDashboardStats(c, fromDate, toDate),
          api.getReports(null, null, c && c !== 'All' ? c : null, fromDate, toDate)
        ];
        if (isAdmin) promises.push(api.getFacultyComparison(c));
        if (isAHOrAbove) promises.push(api.getCICComparison(c));
        const results = await Promise.all(promises);
        setStats(results[0].data);
        setAllReports(results[1].data);
        let idx = 2;
        if (isAdmin && results[idx]) { setFacultyComparison(results[idx].data); idx++; }
        if (isAHOrAbove && results[idx]) setCicComparison(results[idx].data);
      } catch (err) {
        console.error('Dashboard load error:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [isAdmin, isAHOrAbove, centreFilter, showCentreFilter, fromDate, toDate]);

  if (loading) {
    return (
      <div data-testid="dashboard-loading" className="flex items-center justify-center h-64">
        <div className="text-slate-400 text-sm">Loading dashboard...</div>
      </div>
    );
  }

  const sc = stats?.status_counts || {};
  const personalStats = stats?.personal_stats;

  const handleCardClick = (key) => {
    setActiveCard(activeCard === key ? null : key);
  };

  const totalReportsList = allReports;
  const pendingList = allReports.filter(r => {
    if (user?.role === 'CIC') return r.report_status === 'Submitted';
    if (user?.role === 'AcademicHead') return r.report_status === 'CICForwarded';
    if (user?.role === 'PrincipalSSV' || user?.role === 'SuperAdmin' || user?.role === 'CEO') return r.report_status === 'AHForwarded';
    return ['Submitted', 'CICForwarded', 'AHForwarded'].includes(r.report_status);
  });
  const reviewedList = allReports.filter(r => r.report_status === 'Reviewed');
  const hoursList = allReports.map(r => {
    const hrs = (r.regular_duties || []).reduce((sum, d) => sum + (d.total_hours || 0), 0);
    return { ...r, _totalHrs: hrs };
  }).filter(r => r._totalHrs > 0);

  // Build per-section personal activity lists from MY reports only
  const myReports = allReports.filter(r => r.faculty_id === user?._id);
  const personalAcademicsItems = [];
  const personalRespItems = [];
  const personalInitItems = [];
  myReports.forEach(r => {
    (r.regular_duties || []).forEach(d => {
      if ((d.total_hours || 0) > 0) personalAcademicsItems.push({ _reportId: r._id, _date: r.report_date, activity: d.activity || d.subject || d.topic || '—', hours: d.total_hours || 0, type: d.duty_type || '' });
    });
    (r.responsibilities || []).forEach(d => {
      if ((d.time_spent_hrs || 0) > 0) personalRespItems.push({ _reportId: r._id, _date: r.report_date, activity: d.responsibility || d.description || d.activity || '—', hours: d.time_spent_hrs || 0, type: d.group_name || '' });
    });
    (r.course_marketings || []).forEach(d => {
      if ((d.hours || 0) > 0) personalInitItems.push({ _reportId: r._id, _date: r.report_date, activity: d.activity || d.campaign || d.initiative || '—', hours: d.hours || 0, type: d.platform || d.medium || '' });
    });
  });
  const personalAllItems = [
    ...personalAcademicsItems.map(x => ({ ...x, _section: 'Academics' })),
    ...personalRespItems.map(x => ({ ...x, _section: 'Responsibilities' })),
    ...personalInitItems.map(x => ({ ...x, _section: 'Initiatives' })),
  ];

  const statCards = [
    { key: 'total', label: 'Total Reports', value: stats?.total_reports || 0, icon: FileText, color: 'text-slate-900', bg: 'bg-slate-50', activeBorder: 'border-slate-900', activeRing: 'ring-slate-900/10' },
    { key: 'pending', label: 'Pending to Review', value: stats?.pending_approvals || 0, icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50', activeBorder: 'border-amber-500', activeRing: 'ring-amber-500/10' },
    { key: 'reviewed', label: 'Reviewed', value: sc.Reviewed || 0, icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50', activeBorder: 'border-emerald-500', activeRing: 'ring-emerald-500/10' },
    { key: 'leaves', label: 'Leaves This Week', value: stats?.current_week_leaves || 0, icon: Coffee, color: 'text-amber-700', bg: 'bg-amber-50', activeBorder: 'border-amber-500', activeRing: 'ring-amber-500/10' },
  ];



  // Build faculty hours bar chart data from comparison data
  const filteredComparison = roleFilter === 'All'
    ? facultyComparison
    : facultyComparison.filter(f => f.role === roleFilter);

  // Sort filteredComparison for Staff Comparison table — ACTIVE always come before INACTIVE,
  // then by user-selected sort column. When no sort column selected, defaults to active-first → total_hours desc.
  const sortedComparison = (() => {
    const arr = [...filteredComparison];
    const activeOf = (x) => (x.is_active !== false) ? 0 : 1;
    if (!sortCol) {
      arr.sort((a, b) => {
        const da = activeOf(a) - activeOf(b);
        if (da !== 0) return da;
        return (b.total_hours || 0) - (a.total_hours || 0);
      });
      return arr;
    }
    const cmp = (a, b) => {
      // Active-first always wins
      const da = activeOf(a) - activeOf(b);
      if (da !== 0) return da;
      const get = (x) => {
        switch (sortCol) {
          case 'name': return (x.faculty_name || '').toLowerCase();
          case 'role': return (x.role || '').toLowerCase();
          case 'reports': return x.total_reports || 0;
          case 'academics': return x.academics_hours || 0;
          case 'resp': return x.resp_mktg_hours || 0;
          case 'total': return x.total_hours || 0;
          case 'leaves': return x.leaves_count || 0;
          case 'latest': return x.latest_week || '';
          case 'status': return x.latest_status || '';
          case 'overdue': return x.overdue_count || 0;
          default: return 0;
        }
      };
      const va = get(a), vb = get(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    };
    arr.sort(cmp);
    return arr;
  })();

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };
  const sortIcon = (col) => sortCol === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ⇅';

  const facultyHoursChart = filteredComparison.map(f => ({
    name: f.faculty_name.split(' ').slice(-1)[0],
    fullName: f.faculty_name,
    academics: Math.round((f.academics_hours || 0) * 10) / 10,
    resp_mktg: Math.round((f.resp_mktg_hours || 0) * 10) / 10,
    total: Math.round((f.total_hours || 0) * 10) / 10,
    leaves: f.leaves_count || 0,
    role: f.role,
  }));

  // Determine available role filters based on current user's hierarchy
  const getVisibleRoleFilters = () => {
    if (user?.role === 'CIC') return ['All', 'Faculty'];
    if (user?.role === 'AcademicHead') return ['All', 'Faculty', 'CIC'];
    if (isSuperOrPrincipal) return ['All', 'Faculty', 'CIC', 'AcademicHead', 'PrincipalSSV'];
    return ['All'];
  };
  const roleFilters = getVisibleRoleFilters();

  // Render the detail panel for the active card
  const renderDetailPanel = () => {
    if (!activeCard) return null;

    let title = '';
    let items = [];
    let emptyMsg = '';
    let columns = [];

    if (activeCard === 'total') {
      title = 'All Reports'; items = totalReportsList; emptyMsg = 'No reports created yet.'; columns = ['week', 'faculty', 'status', 'submitted'];
    } else if (activeCard === 'hours') {
      title = 'Hours Breakdown by Report'; items = hoursList; emptyMsg = 'No hours logged yet.'; columns = ['week', 'faculty', 'hours', 'status'];
    } else if (activeCard === 'pending') {
      title = 'Pending to Review'; items = pendingList; emptyMsg = 'No reports pending review.'; columns = ['week', 'faculty', 'status', 'submitted'];
    } else if (activeCard === 'reviewed') {
      title = 'Reviewed Reports'; items = reviewedList; emptyMsg = 'No reviewed reports yet.'; columns = ['week', 'faculty', 'status', 'submitted'];
    } else if (activeCard === 'leaves') {
      // Show leave entries from regular_duties marked as leave
      const leaveItems = [];
      allReports.forEach(r => {
        (r.regular_duties || []).forEach(d => {
          if ((d.duty_type || '').toLowerCase().includes('leave') || (d.activity || '').toLowerCase().includes('leave')) {
            leaveItems.push({ _reportId: r._id, _date: r.report_date, faculty_name: r.faculty_name, activity: d.activity || d.duty_type || 'Leave', hours: d.total_hours || 0 });
          }
        });
      });
      return (
        <Card data-testid="detail-panel-leaves" className="bg-white border-2 border-amber-500 ring-4 ring-amber-500/10 shadow-md animate-fade-in">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Coffee className="w-4 h-4 text-amber-700" />
                <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600">Leave Entries This Week</CardTitle>
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-semibold">{leaveItems.length}</span>
              </div>
              <button data-testid="close-detail-panel" onClick={() => setActiveCard(null)} className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-100 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
          </CardHeader>
          <CardContent>
            {leaveItems.length > 0 ? (
              <div className="max-h-[320px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b border-slate-200">
                      <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-400 pb-2 pr-3">Date</th>
                      <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-400 pb-2 pr-3">Faculty</th>
                      <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-400 pb-2">Activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaveItems.map((it, i) => (
                      <tr key={i} onClick={() => navigate(`/reports/${it._reportId}`)} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer">
                        <td className="py-2.5 pr-3 text-slate-700">{it._date}</td>
                        <td className="py-2.5 pr-3 text-slate-700">{it.faculty_name}</td>
                        <td className="py-2.5 text-slate-600">{it.activity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="text-sm text-slate-400 italic text-center py-6">No leave entries in selected period.</p>}
          </CardContent>
        </Card>
      );
    } else if (['p-academics', 'p-resp', 'p-init', 'p-total'].includes(activeCard)) {
      const sectionConfig = {
        'p-academics': { title: 'My Academic Duties', items: personalAcademicsItems, color: 'text-blue-700', border: 'border-blue-500', ring: 'ring-blue-500/10' },
        'p-resp': { title: 'My Responsibilities', items: personalRespItems, color: 'text-indigo-700', border: 'border-indigo-500', ring: 'ring-indigo-500/10' },
        'p-init': { title: 'My Initiatives', items: personalInitItems, color: 'text-emerald-700', border: 'border-emerald-500', ring: 'ring-emerald-500/10' },
        'p-total': { title: 'My Total Hours Breakdown', items: personalAllItems, color: 'text-slate-900', border: 'border-slate-500', ring: 'ring-slate-500/10' },
      }[activeCard];
      const totalH = sectionConfig.items.reduce((s, x) => s + (x.hours || 0), 0);
      return (
        <Card data-testid={`detail-panel-${activeCard}`} className={`bg-white border-2 ${sectionConfig.border} ring-4 ${sectionConfig.ring} shadow-md animate-fade-in`}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600">{sectionConfig.title}</CardTitle>
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-semibold">{sectionConfig.items.length} items · {Math.round(totalH * 10) / 10}h</span>
              </div>
              <button data-testid="close-detail-panel" onClick={() => setActiveCard(null)} className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-100 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
          </CardHeader>
          <CardContent>
            {sectionConfig.items.length > 0 ? (
              <div className="max-h-[320px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b border-slate-200">
                      <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-400 pb-2 pr-3">Date</th>
                      {activeCard === 'p-total' && <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-400 pb-2 pr-3">Section</th>}
                      <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-400 pb-2 pr-3">Activity</th>
                      <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-400 pb-2 pr-3">Type</th>
                      <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-400 pb-2">Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sectionConfig.items.map((it, i) => (
                      <tr key={i} onClick={() => it._reportId && navigate(`/reports/${it._reportId}`)} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer">
                        <td className="py-2.5 pr-3 text-slate-700">{it._date}</td>
                        {activeCard === 'p-total' && <td className="py-2.5 pr-3 text-slate-700 font-semibold">{it._section}</td>}
                        <td className="py-2.5 pr-3 text-slate-700">{it.activity}</td>
                        <td className="py-2.5 pr-3 text-slate-500 text-xs">{it.type || '—'}</td>
                        <td className="py-2.5 text-right font-bold text-blue-700">{it.hours}h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="text-sm text-slate-400 italic text-center py-6">No personal entries logged in selected period.</p>}
          </CardContent>
        </Card>
      );
    }

    const activeConfig = statCards.find(c => c.key === activeCard);

    return (
      <Card data-testid={`detail-panel-${activeCard}`} className={`bg-white border-2 ${activeConfig?.activeBorder || 'border-slate-200'} shadow-md animate-fade-in`}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {activeConfig && <activeConfig.icon className={`w-4 h-4 ${activeConfig.color}`} />}
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600">{title}</CardTitle>
              <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-semibold">{items.length}</span>
            </div>
            <button data-testid="close-detail-panel" onClick={() => setActiveCard(null)} className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-100 rounded transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </CardHeader>
        <CardContent>
          {items.length > 0 ? (
            <div className="overflow-x-auto max-h-[320px] overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b border-slate-200">
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-400 pb-2 pr-4">Week</th>
                    {user?.role !== 'Faculty' && columns.includes('faculty') && (
                      <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-400 pb-2 pr-4">Faculty</th>
                    )}
                    {columns.includes('hours') && (
                      <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-400 pb-2 pr-4">Hours</th>
                    )}
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-400 pb-2 pr-4">Status</th>
                    {columns.includes('submitted') && (
                      <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-400 pb-2">Submitted</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {items.map(r => (
                    <tr key={r._id} onClick={() => navigate(`/reports/${r._id}`)} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors">
                      <td className="py-2.5 pr-4 text-sm text-slate-700">{r.report_date || `${r.week_from} — ${r.week_to}`}</td>
                      {user?.role !== 'Faculty' && columns.includes('faculty') && (
                        <td className="py-2.5 pr-4 text-sm text-slate-600">{r.faculty_name}</td>
                      )}
                      {columns.includes('hours') && (
                        <td className="py-2.5 pr-4 text-sm font-bold text-blue-600">{r._totalHrs || 0}h</td>
                      )}
                      <td className="py-2.5 pr-4"><StatusBadge status={r.report_status} /></td>
                      {columns.includes('submitted') && (
                        <td className="py-2.5 text-sm text-slate-500">{r.submitted_date ? new Date(r.submitted_date).toLocaleDateString() : '—'}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400 text-sm">{emptyMsg}</div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div data-testid="dashboard-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">Dashboard</p>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Welcome, {user?.name}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {ROLE_LABELS[user?.role] || user?.role} &middot; Report Overview
          </p>
        </div>
        {canCreateReport && (
          <button
            data-testid="dashboard-new-report-btn"
            onClick={() => navigate('/reports/new')}
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-md font-medium px-5 py-2.5 text-sm transition-colors"
          >
            + New Report
          </button>
        )}
      </div>

      {/* Weekly Summary deadline alerts (CIC + Head) */}
      {/* Clarification SLA banner (>24h responses) */}
      {(clarAlerts.responder_items.length > 0 || clarAlerts.requester_items.length > 0) && (
        <div data-testid="clarification-sla-banner" className="space-y-2">
          {clarAlerts.responder_items.length > 0 && (
            <div data-testid="sla-responder-alert" className={`flex items-start gap-3 rounded-lg border-l-4 p-3.5 ${clarAlerts.responder_items.some(i => i.is_critical) ? 'bg-red-50 border-red-500' : 'bg-amber-50 border-amber-500'}`}>
              <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${clarAlerts.responder_items.some(i => i.is_critical) ? 'bg-red-500' : 'bg-amber-500'}`}>
                <Bell className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-bold ${clarAlerts.responder_items.some(i => i.is_critical) ? 'text-red-900' : 'text-amber-900'}`}>
                  {clarAlerts.responder_items.length} clarification request{clarAlerts.responder_items.length === 1 ? '' : 's'} pending your response &gt;24h
                </p>
                <p className={`text-xs mt-0.5 ${clarAlerts.responder_items.some(i => i.is_critical) ? 'text-red-700' : 'text-amber-800'}`}>
                  {clarAlerts.responder_items.slice(0, 3).map(r => `${r.summary_type} (${Math.floor(r.hours_overdue)}h)`).join(', ')}
                  {clarAlerts.responder_items.length > 3 && ` +${clarAlerts.responder_items.length - 3} more`}
                </p>
              </div>
              <button data-testid="sla-respond-btn" onClick={() => navigate('/weekly-summary')} className="shrink-0 text-xs font-bold px-3 py-1.5 rounded-md inline-flex items-center gap-1 transition-colors bg-red-600 text-white hover:bg-red-700">
                Respond <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          )}
          {clarAlerts.requester_items.length > 0 && (
            <div data-testid="sla-requester-alert" className="flex items-start gap-3 rounded-lg border-l-4 p-3.5 bg-orange-50 border-orange-500">
              <div className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-orange-500">
                <Bell className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-orange-900">
                  {clarAlerts.requester_items.length} of your clarification request{clarAlerts.requester_items.length === 1 ? '' : 's'} unanswered &gt;24h
                </p>
                <p className="text-xs mt-0.5 text-orange-800">
                  {clarAlerts.requester_items.slice(0, 3).map(r => `${r.summary_type} → ${r.owner_name} (${Math.floor(r.hours_overdue)}h)`).join(', ')}
                  {clarAlerts.requester_items.length > 3 && ` +${clarAlerts.requester_items.length - 3} more`}
                </p>
              </div>
              <button data-testid="sla-followup-btn" onClick={() => navigate('/weekly-summary')} className="shrink-0 text-xs font-bold px-3 py-1.5 rounded-md inline-flex items-center gap-1 transition-colors bg-orange-600 text-white hover:bg-orange-700">
                Follow up <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      )}

      {weeklyAlerts.length > 0 && (
        <div data-testid="weekly-summary-alerts" className="space-y-2">
          {weeklyAlerts.map((a, i) => {
            const isCritical = a.level === 'critical';
            return (
              <div
                key={i}
                data-testid={`weekly-alert-${a.type}`}
                className={`flex items-start gap-3 rounded-lg border-l-4 p-3.5 ${
                  isCritical
                    ? 'bg-red-50 border-red-500 text-red-900'
                    : 'bg-amber-50 border-amber-500 text-amber-900'
                }`}
              >
                <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${isCritical ? 'bg-red-500' : 'bg-amber-500'}`}>
                  <Bell className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-bold ${isCritical ? 'text-red-900' : 'text-amber-900'}`}>{a.title}</p>
                  <p className={`text-xs mt-0.5 ${isCritical ? 'text-red-700' : 'text-amber-800'}`}>{a.message}</p>
                </div>
                <button
                  data-testid={`weekly-alert-action-${a.type}`}
                  onClick={() => navigate(a.action_link)}
                  className={`shrink-0 text-xs font-bold px-3 py-1.5 rounded-md inline-flex items-center gap-1 transition-colors ${
                    isCritical ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-amber-600 text-white hover:bg-amber-700'
                  }`}
                >
                  {a.action_label || 'Open'} <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Centre Filter (Principal/CEO/SuperAdmin/AcademicHead) */}
      {showCentreFilter && centres.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="dashboard-centre-filter">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Centre:</span>
          {['All', ...centres].map((c) => (
            <button
              key={c}
              data-testid={`dashboard-centre-${c.toLowerCase()}`}
              onClick={() => setCentreFilter(c)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                centreFilter === c
                  ? 'bg-emerald-600 text-white'
                  : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
              }`}
            >
              {c === 'All' ? 'All Centres' : c}
            </button>
          ))}
        </div>
      )}

      {/* Date Range Filter — applies to ALL data on this dashboard */}
      <div className="flex items-center gap-2 flex-wrap bg-white border border-slate-200 rounded-lg p-3" data-testid="dashboard-date-range">
        <div className="flex items-center gap-2 mr-2">
          <Calendar className="w-4 h-4 text-slate-500" />
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Period</span>
        </div>
        <input
          data-testid="from-date-input"
          type="date"
          value={fromDate}
          onChange={e => setFromDate(e.target.value)}
          className="text-xs border border-slate-300 rounded-md px-2 py-1 focus:outline-none focus:border-blue-500"
        />
        <span className="text-xs text-slate-400">to</span>
        <input
          data-testid="to-date-input"
          type="date"
          value={toDate}
          onChange={e => setToDate(e.target.value)}
          className="text-xs border border-slate-300 rounded-md px-2 py-1 focus:outline-none focus:border-blue-500"
        />
        <div className="flex items-center gap-1 ml-2 flex-wrap">
          {[
            { label: 'This Week', from: () => { const t = new Date(); const m = new Date(t); m.setDate(t.getDate() - t.getDay() + (t.getDay() === 0 ? -6 : 1)); return m.toISOString().split('T')[0]; }, to: () => new Date().toISOString().split('T')[0] },
            { label: 'This Month', from: () => new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0], to: () => new Date().toISOString().split('T')[0] },
            { label: 'Last 30d', from: () => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split('T')[0]; }, to: () => new Date().toISOString().split('T')[0] },
            { label: 'All Time', from: () => '2024-01-01', to: () => new Date().toISOString().split('T')[0] },
          ].map(preset => (
            <button
              key={preset.label}
              data-testid={`preset-${preset.label.toLowerCase().replace(/\s/g, '-')}`}
              onClick={() => { setFromDate(preset.from()); setToDate(preset.to()); }}
              className="text-[11px] px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold transition-colors"
            >{preset.label}</button>
          ))}
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {statCards.map((card) => {
          const Icon = card.icon;
          const isActive = activeCard === card.key;
          return (
            <Card
              key={card.key}
              data-testid={`stat-card-${card.label.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => handleCardClick(card.key)}
              className={`bg-white shadow-sm cursor-pointer transition-all duration-200 ${
                isActive
                  ? `border-2 ${card.activeBorder} ring-4 ${card.activeRing} scale-[1.02]`
                  : 'border border-slate-200 hover:border-slate-300 hover:shadow-md'
              }`}
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{card.label}</p>
                    <p className={`text-3xl font-black mt-1 ${card.color}`} style={{ fontFamily: 'Chivo, sans-serif' }}>{card.value}</p>
                  </div>
                  <div className={`w-11 h-11 rounded-lg ${card.bg} flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 ${card.color}`} />
                  </div>
                </div>
                <p className="text-[10px] text-slate-400 mt-2 font-medium">
                  {isActive ? 'Click to close' : 'Click for details'}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {renderDetailPanel()}

      {/* CEO Insights Panel (replaces personal hours for CEO/SuperAdmin) */}
      {stats && ['CEO', 'SuperAdmin'].includes(user?.role) && (
        <CEOInsightsPanel fromDate={fromDate} toDate={toDate} onCentreClick={() => setShowStaffStatus(true)} centreStaffStatus={centreStaffStatus} />
      )}

      {/* Personal Hours Breakdown (Academics / Responsibilities / Initiatives / Total) + Centre Staff Status (non-Faculty only) */}
      {stats && (!['CEO', 'SuperAdmin'].includes(user?.role) || user?.also_faculty) && (
        <div data-testid="personal-hours-section">
          {['CEO', 'SuperAdmin'].includes(user?.role) && user?.also_faculty && (
            <div className="flex items-center gap-2 mb-2 mt-2">
              <FileText className="w-4 h-4 text-blue-600" />
              <h3 className="text-sm font-bold uppercase tracking-wider text-blue-600">My Work (Personal Reports)</h3>
            </div>
          )}
          <div className={`grid grid-cols-2 ${user?.role === 'Faculty' ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3 lg:grid-cols-5'} gap-3`} data-testid="personal-hours-breakdown">
          <Card onClick={() => handleCardClick('p-academics')} className={`shadow-sm cursor-pointer transition-all ${activeCard === 'p-academics' ? 'border-2 border-blue-500 ring-4 ring-blue-500/10 scale-[1.02]' : 'border border-slate-200 hover:border-blue-300 hover:shadow-md'}`} data-testid="personal-academics-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-blue-50 flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-700" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Academic Duties (Period)</p>
                <p className="text-2xl font-black text-blue-700" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {(stats.personal_breakdown?.academics_hours ?? 0)}<span className="text-sm text-slate-400 font-bold ml-0.5">h</span>
                </p>
                <p className="text-[10px] text-blue-600 mt-0.5 font-semibold">Click for details</p>
              </div>
            </CardContent>
          </Card>
          <Card onClick={() => handleCardClick('p-resp')} className={`shadow-sm cursor-pointer transition-all ${activeCard === 'p-resp' ? 'border-2 border-indigo-500 ring-4 ring-indigo-500/10 scale-[1.02]' : 'border border-slate-200 hover:border-indigo-300 hover:shadow-md'}`} data-testid="personal-responsibilities-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-indigo-50 flex items-center justify-center">
                <FileText className="w-5 h-5 text-indigo-700" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Responsibilities (Period)</p>
                <p className="text-2xl font-black text-indigo-700" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {(stats.personal_breakdown?.responsibilities_hours ?? 0)}<span className="text-sm text-slate-400 font-bold ml-0.5">h</span>
                </p>
                <p className="text-[10px] text-indigo-600 mt-0.5 font-semibold">Click for details</p>
              </div>
            </CardContent>
          </Card>
          <Card onClick={() => handleCardClick('p-init')} className={`shadow-sm cursor-pointer transition-all ${activeCard === 'p-init' ? 'border-2 border-emerald-500 ring-4 ring-emerald-500/10 scale-[1.02]' : 'border border-slate-200 hover:border-emerald-300 hover:shadow-md'}`} data-testid="personal-initiatives-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-emerald-50 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Initiatives (Period)</p>
                <p className="text-2xl font-black text-emerald-700" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {(stats.personal_breakdown?.initiatives_hours ?? 0)}<span className="text-sm text-slate-400 font-bold ml-0.5">h</span>
                </p>
                <p className="text-[10px] text-emerald-600 mt-0.5 font-semibold">Click for details</p>
              </div>
            </CardContent>
          </Card>
          <Card onClick={() => handleCardClick('p-total')} className={`shadow-sm cursor-pointer transition-all ${activeCard === 'p-total' ? 'border-2 border-slate-700 ring-4 ring-slate-500/10 scale-[1.02]' : 'border-2 border-slate-300 bg-slate-50 hover:shadow-md'}`} data-testid="personal-total-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-slate-200 flex items-center justify-center">
                <Sigma className="w-5 h-5 text-slate-700" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Total Hours (Period)</p>
                <p className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {(stats.personal_breakdown?.total_hours ?? 0)}<span className="text-sm text-slate-500 font-bold ml-0.5">h</span>
                </p>
                <p className="text-[10px] text-blue-600 mt-0.5 font-semibold">Click for details</p>
              </div>
            </CardContent>
          </Card>
          {user?.role !== 'Faculty' && !['CEO', 'SuperAdmin'].includes(user?.role) && (
            <Card data-testid="centre-staff-status-card" onClick={() => setShowStaffStatus(true)} className="border border-slate-200 shadow-sm cursor-pointer hover:border-blue-400 hover:shadow-md transition-all">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-blue-50 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-700" />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
                    {user?.role === 'CIC' ? 'My Team Update Status' : 'Centre Staff Update Status'}
                  </p>
                  <p className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    <span className="text-emerald-600">{centreStaffStatus?.active ?? '—'}</span>
                    <span className="text-slate-400 text-base"> / {centreStaffStatus?.total ?? '—'}</span>
                  </p>
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    {centreStaffStatus?.inactive ?? 0} not updating · <span className="text-blue-600 font-semibold">Click for details</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        </div>
      )}

      {/* Personal Stats for Admin Roles */}
      {personalStats && isAdmin && user?.role !== 'SuperAdmin' && user?.role !== 'CEO' && (
        <Card data-testid="personal-stats-card" className="bg-white border-2 border-blue-200 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-600" />
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-blue-600">My Reports &amp; Hours</CardTitle>
              <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-semibold">{personalStats.total_reports} reports, {Math.round(personalStats.total_hours)}h</span>
            </div>
          </CardHeader>
          <CardContent>
            {personalStats.weekly_trend?.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={personalStats.weekly_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                  <Line type="monotone" dataKey="hours" stroke="#1D4ED8" strokeWidth={2} dot={{ fill: '#1D4ED8', r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[120px] flex items-center justify-center text-slate-400 text-sm">No personal reports yet. Create your first daily report!</div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hours Trend (all visible) */}
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-600" />
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500">
                {user?.role === 'Faculty' ? 'My Hours Trend' : 'Overall Hours Trend'}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {stats?.weekly_trend?.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={stats.weekly_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                  <Line type="monotone" dataKey="hours" stroke="#1D4ED8" strokeWidth={2} dot={{ fill: '#1D4ED8', r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-slate-400 text-sm">No data yet</div>
            )}
          </CardContent>
        </Card>

        {/* Activity Overview */}
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-600" />
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500">Activity Overview</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(sc).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <StatusBadge status={status} />
                  <span className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Chivo, sans-serif' }}>{count}</span>
                </div>
              ))}
              {Object.keys(sc).length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">No reports yet</p>
              )}
              {allReports.length > 0 && (
                <div className="border-t border-slate-100 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Avg Hours / Report</p>
                      <p className="text-xl font-black text-slate-900 mt-0.5" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        {allReports.length > 0 ? Math.round((stats?.total_hours || 0) / allReports.length * 10) / 10 : 0}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400" title="Sum of Academic duties + Responsibilities + Initiatives entries">Activity Entries</p>
                      <p className="text-xl font-black text-slate-900 mt-0.5" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        {stats?.entries_breakdown?.total ?? allReports.reduce((sum, r) => sum + (r.regular_duties?.length || 0) + (r.responsibilities?.length || 0) + (r.course_marketings?.length || 0), 0)}
                      </p>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {(stats?.entries_breakdown?.duties || 0)} duties · {(stats?.entries_breakdown?.responsibilities || 0)} resp · {(stats?.entries_breakdown?.marketing || 0)} init
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Staff Work Hours Bar Chart — Admin roles only */}
      {isAdmin && facultyHoursChart.length > 0 && (
        <Card data-testid="faculty-hours-chart" className="bg-white border border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-700" />
                <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500">Staff Work Hours</CardTitle>
                <span className="text-xs text-slate-400 font-medium">{filteredComparison.length} staff</span>
              </div>
              {/* Role filter pills */}
              {roleFilters.length > 1 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  {roleFilters.map(r => {
                    const slug = r === 'AcademicHead' ? 'head' : r === 'PrincipalSSV' ? 'principal' : r.toLowerCase();
                    return (
                    <button
                      key={r}
                      data-testid={`role-filter-${slug}`}
                      onClick={() => setRoleFilter(r)}
                      className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${
                        roleFilter === r
                          ? 'bg-slate-900 text-white'
                          : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                      }`}
                    >
                      {r === 'All' ? 'All' : ROLE_LABELS[r] || r}
                    </button>
                  );})}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {facultyHoursChart.length >= 30 ? (
              // Many staff: vertical bars with horizontal scroll
              <div className="overflow-x-auto">
                <div style={{ minWidth: Math.max(600, facultyHoursChart.length * 38) }}>
                  <ResponsiveContainer width="100%" height={420}>
                    <BarChart data={facultyHoursChart} margin={{ left: 10, right: 10, top: 16, bottom: 80 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#475569' }} interval={0} angle={-45} textAnchor="end" height={80} />
                      <YAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                      <Tooltip
                        formatter={(value, name) => [`${value}h`, name === 'academics' ? 'Academics' : 'Resp / Init']}
                        contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                        labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} formatter={(value) => value === 'academics' ? 'Academics' : 'Resp / Init'} />
                      <Bar dataKey="academics" stackId="hours" fill="#1D4ED8" barSize={16} />
                      <Bar dataKey="resp_mktg" stackId="hours" fill="#10B981" barSize={16} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-[10px] text-slate-400 mt-2 text-center">{facultyHoursChart.length} staff — scroll horizontally to view all</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(220, facultyHoursChart.length * 42)}>
                <BarChart data={facultyHoursChart} layout="vertical" margin={{ left: 10, right: 60, top: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#475569' }} width={90} />
                  <Tooltip
                    formatter={(value, name) => [`${value}h`, name === 'academics' ? 'Academics' : 'Resp / Init']}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                    labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} formatter={(value) => value === 'academics' ? 'Academics' : 'Resp / Init'} />
                  <Bar dataKey="academics" stackId="hours" fill="#1D4ED8" barSize={18} />
                  <Bar dataKey="resp_mktg" stackId="hours" fill="#10B981" barSize={18} radius={[0, 4, 4, 0]}>
                    <LabelList dataKey="total" position="right" formatter={(v) => `${v}h`} style={{ fontSize: 11, fontWeight: 700, fill: '#0f172a' }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Staff Submission Report — SuperAdmin/CEO */}
      {(user?.role === 'SuperAdmin' || user?.role === 'CEO') && (() => {
        // Build per-staff submission status
        const today = new Date().toISOString().split('T')[0];
        const staffMap = {};
        // Get all users who should submit reports
        const reportCreators = facultyComparison.length > 0 ? facultyComparison : [];

        reportCreators.forEach(f => {
          staffMap[f.faculty_id] = {
            name: f.faculty_name,
            role: f.role || 'Faculty',
            totalReports: f.total_reports,
            totalHours: f.total_hours,
            latestDate: f.latest_week || '',
            latestStatus: f.latest_status || '',
            hasSubmittedToday: false,
          };
        });

        // Check today's submissions
        allReports.forEach(r => {
          const fid = r.faculty_id;
          if (staffMap[fid] && r.report_date === today) {
            staffMap[fid].hasSubmittedToday = true;
          }
        });

        const staffList = Object.values(staffMap).sort((a, b) => a.name.localeCompare(b.name));
        const submittedToday = staffList.filter(s => s.hasSubmittedToday);
        const notSubmittedToday = staffList.filter(s => !s.hasSubmittedToday);
        const neverSubmitted = staffList.filter(s => s.totalReports === 0);

        return (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-bold uppercase tracking-wider text-emerald-600">Submitted Today</CardTitle>
                  <span className="text-xs font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">{submittedToday.length}</span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="max-h-[300px] overflow-y-auto space-y-1">
                  {submittedToday.length > 0 ? submittedToday.map((s, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50">
                      <span className="text-xs font-medium text-slate-700">{s.name}</span>
                      <span className="text-[10px] text-emerald-600 font-semibold">{s.totalHours}h total</span>
                    </div>
                  )) : <p className="text-xs text-slate-400 text-center py-4">No submissions today</p>}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border border-red-100 shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-bold uppercase tracking-wider text-red-600">Not Submitted Today</CardTitle>
                  <span className="text-xs font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{notSubmittedToday.length}</span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="max-h-[300px] overflow-y-auto space-y-1">
                  {notSubmittedToday.length > 0 ? notSubmittedToday.map((s, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50">
                      <span className="text-xs font-medium text-slate-700">{s.name}</span>
                      <span className="text-[10px] text-slate-400">{s.latestDate ? `Last: ${s.latestDate}` : 'Never'}</span>
                    </div>
                  )) : <p className="text-xs text-slate-400 text-center py-4">Everyone submitted!</p>}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border border-amber-100 shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-bold uppercase tracking-wider text-amber-600">Never Submitted</CardTitle>
                  <span className="text-xs font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{neverSubmitted.length}</span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="max-h-[300px] overflow-y-auto space-y-1">
                  {neverSubmitted.length > 0 ? neverSubmitted.map((s, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50">
                      <span className="text-xs font-medium text-slate-700">{s.name}</span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${ROLE_LABELS[s.role] ? 'bg-slate-100 text-slate-500' : ''}`}>{s.role}</span>
                    </div>
                  )) : <p className="text-xs text-slate-400 text-center py-4">All staff have submitted</p>}
                </div>
              </CardContent>
            </Card>
          </div>
        );
      })()}

      {/* Responsibility-wise Hours — non-SuperAdmin/CEO */}
      {user?.role !== 'SuperAdmin' && user?.role !== 'CEO' && (() => {
        const groupHoursMap = {};
        allReports.forEach(r => {
          (r.responsibilities || []).forEach(resp => {
            const gn = resp.group_name || 'General';
            if (!groupHoursMap[gn]) groupHoursMap[gn] = { totalHours: 0, entries: 0 };
            groupHoursMap[gn].totalHours += (resp.time_spent_hrs || 0);
            groupHoursMap[gn].entries += 1;
          });
        });
        const groupData = Object.entries(groupHoursMap)
          .map(([name, d]) => ({ name, hours: Math.round(d.totalHours * 10) / 10, entries: d.entries }))
          .sort((a, b) => b.hours - a.hours);

        const totalRespHours = groupData.reduce((s, g) => s + g.hours, 0);
        const maxHours = groupData.length > 0 ? groupData[0].hours : 1;
        const COLORS = ['#1D4ED8', '#0F172A', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];

        if (groupData.length === 0) return null;

        return (
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-slate-700" />
                  <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500">Responsibility Hours Summary</CardTitle>
                </div>
                <span className="text-xs font-semibold text-slate-400">{totalRespHours} hrs total</span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {groupData.map((g, i) => {
                  const pct = maxHours > 0 ? (g.hours / maxHours) * 100 : 0;
                  return (
                    <div key={g.name} className="flex items-center gap-4">
                      <div className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between mb-1">
                          <p className="text-sm font-medium text-slate-700 truncate pr-3">{g.name}</p>
                          <div className="flex items-center gap-3 shrink-0">
                            <span className="text-xs text-slate-400">{g.entries} entries</span>
                            <span className="text-sm font-bold text-slate-900 tabular-nums w-12 text-right">{g.hours}h</span>
                          </div>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* CIC Forwarded Reports - Academic Head view */}
      {user?.role === 'AcademicHead' && (() => {
        const forwarded = allReports.filter(r => r.report_status === 'CICForwarded');
        if (forwarded.length === 0) return null;
        return (
          <Card data-testid="forwarded-reports-card" className="bg-white border-2 border-indigo-200 shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-indigo-600" />
                  <CardTitle className="text-sm font-bold uppercase tracking-wider text-indigo-600">Forwarded by CIC — Awaiting Your Review</CardTitle>
                </div>
                <span className="text-xs font-bold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">{forwarded.length} pending</span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 pb-2.5 pr-4">Faculty</th>
                      <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 pb-2.5 pr-4">Period</th>
                      <th className="text-center text-[10px] font-bold uppercase tracking-wider text-slate-400 pb-2.5 px-3">Hours</th>
                      <th className="text-center text-[10px] font-bold uppercase tracking-wider text-slate-400 pb-2.5 px-3">Sections</th>
                      <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 pb-2.5 px-3">CIC Comments</th>
                      <th className="pb-2.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {forwarded.map(r => {
                      const hrs = (r.regular_duties || []).reduce((s, d) => s + (d.total_hours || 0), 0);
                      const sectionCount = (r.regular_duties?.length || 0) + (r.responsibilities?.length || 0) + (r.course_marketings?.length || 0);
                      return (
                        <tr key={r._id} className="border-b border-slate-100 hover:bg-indigo-50/30 transition-colors cursor-pointer" onClick={() => navigate(`/reports/${r._id}`)}>
                          <td className="py-3 pr-4 font-semibold text-slate-800">{r.faculty_name}</td>
                          <td className="py-3 pr-4 text-slate-600">{r.report_date || `${r.week_from} — ${r.week_to}`}</td>
                          <td className="py-3 px-3 text-center font-bold text-blue-600">{hrs}h</td>
                          <td className="py-3 px-3 text-center text-slate-500">{sectionCount} rows</td>
                          <td className="py-3 px-3 text-slate-500 text-xs max-w-[200px] truncate">{r.cic_comments || '—'}</td>
                          <td className="py-3"><span className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">Review &rarr;</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* CIC Performance Comparison */}
      {isAHOrAbove && cicComparison.length > 0 && (
        <Card data-testid="cic-comparison-card" className="bg-white border border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-indigo-600" />
                <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500">CIC Performance Comparison</CardTitle>
              </div>
              <span className="text-xs text-slate-400 font-medium">{cicComparison.length} CIC(s)</span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4">
              {cicComparison.map(cic => {
                const completionColor = cic.completion_rate >= 80 ? 'text-emerald-600' : cic.completion_rate >= 50 ? 'text-amber-600' : 'text-red-600';
                const completionBg = cic.completion_rate >= 80 ? 'bg-emerald-500' : cic.completion_rate >= 50 ? 'bg-amber-500' : 'bg-red-500';
                return (
                  <div key={cic.cic_id} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center">
                          <span className="text-sm font-bold text-indigo-700">{cic.cic_name.charAt(0)}</span>
                        </div>
                        <div>
                          <p className="text-sm font-bold text-slate-800">{cic.cic_name}</p>
                          <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">CIC</p>
                        </div>
                      </div>
                      {cic.overdue_items > 0 && (
                        <span className="flex items-center gap-1 text-xs font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-md border border-red-200">
                          <AlertTriangle className="w-3 h-3" /> {cic.overdue_items} overdue
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                      <div className="bg-slate-50 rounded-md p-2.5">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Forwarded</p>
                        <p className="text-lg font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>{cic.reports_forwarded}</p>
                      </div>
                      <div className="bg-slate-50 rounded-md p-2.5">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Pending</p>
                        <p className="text-lg font-black text-amber-600" style={{ fontFamily: 'Chivo, sans-serif' }}>{cic.reports_pending}</p>
                      </div>
                      <div className="bg-slate-50 rounded-md p-2.5">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Avg Forward</p>
                        <p className="text-lg font-black text-blue-600" style={{ fontFamily: 'Chivo, sans-serif' }}>{cic.avg_forward_days}d</p>
                      </div>
                      <div className="bg-slate-50 rounded-md p-2.5">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Own Reports</p>
                        <p className="text-lg font-black text-slate-700" style={{ fontFamily: 'Chivo, sans-serif' }}>{cic.own_reports}</p>
                      </div>
                      <div className="bg-slate-50 rounded-md p-2.5">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Completion</p>
                        <div className="flex items-center gap-2">
                          <p className={`text-lg font-black ${completionColor}`} style={{ fontFamily: 'Chivo, sans-serif' }}>{cic.completion_rate}%</p>
                          <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${completionBg}`} style={{ width: `${cic.completion_rate}%` }} />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                      <span>Total faculty hours reviewed: <strong className="text-slate-700">{Math.round(cic.total_faculty_hours * 10) / 10}h</strong></span>
                      <span>{cic.completed_items}/{cic.total_items} items completed</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Faculty Comparison Table — Admin only */}
      {isAdmin && filteredComparison.length > 0 && (
        <Card data-testid="faculty-comparison-card" className="bg-white border border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-700" />
                <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500">Staff Comparison</CardTitle>
              </div>
              <span className="text-xs text-slate-400 font-medium">{filteredComparison.length} staff</span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th onClick={() => toggleSort('name')} data-testid="sort-name" className="cursor-pointer select-none text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-slate-700 pb-2.5 pr-4">Name{sortIcon('name')}</th>
                    <th onClick={() => toggleSort('role')} data-testid="sort-role" className="cursor-pointer select-none text-center text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-slate-700 pb-2.5 px-3">Role{sortIcon('role')}</th>
                    <th onClick={() => toggleSort('reports')} data-testid="sort-reports" className="cursor-pointer select-none text-center text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-slate-700 pb-2.5 px-3">Reports{sortIcon('reports')}</th>
                    <th onClick={() => toggleSort('academics')} data-testid="sort-academics" className="cursor-pointer select-none text-center text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-slate-700 pb-2.5 px-3">Academics{sortIcon('academics')}</th>
                    <th onClick={() => toggleSort('resp')} data-testid="sort-resp" className="cursor-pointer select-none text-center text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-slate-700 pb-2.5 px-3">Resp / Init{sortIcon('resp')}</th>
                    <th onClick={() => toggleSort('total')} data-testid="sort-total" className="cursor-pointer select-none text-center text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-slate-700 pb-2.5 px-3">Total{sortIcon('total')}</th>
                    <th onClick={() => toggleSort('leaves')} data-testid="sort-leaves" className="cursor-pointer select-none text-center text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-slate-700 pb-2.5 px-3">Leaves{sortIcon('leaves')}</th>
                    <th onClick={() => toggleSort('latest')} data-testid="sort-latest" className="cursor-pointer select-none text-center text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-slate-700 pb-2.5 px-3">Latest{sortIcon('latest')}</th>
                    <th onClick={() => toggleSort('status')} data-testid="sort-status" className="cursor-pointer select-none text-center text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-slate-700 pb-2.5 px-3">Status{sortIcon('status')}</th>
                    <th onClick={() => toggleSort('overdue')} data-testid="sort-overdue" className="cursor-pointer select-none text-center text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-slate-700 pb-2.5 px-3">Overdue{sortIcon('overdue')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedComparison.map((f) => (
                    <tr key={f.faculty_id} data-testid={`faculty-row-${f.faculty_id}`} className={`border-b border-slate-100 ${f.is_active === false ? 'bg-slate-50/80 opacity-70' : (f.overdue_count > 0 ? 'bg-red-50/50' : 'hover:bg-slate-50')} transition-colors`}>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-800">{f.faculty_name}</p>
                          {f.is_active === false && (
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 uppercase tracking-wider">Inactive</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-3 text-center">
                        <span className="text-xs font-semibold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{ROLE_LABELS[f.role] || f.role}</span>
                      </td>
                      <td className="py-3 px-3 text-center text-slate-700 font-medium">{f.total_reports}</td>
                      <td className="py-3 px-3 text-center">
                        <span className="font-semibold text-blue-700">{Math.round((f.academics_hours || 0) * 10) / 10}h</span>
                      </td>
                      <td className="py-3 px-3 text-center">
                        <span className="font-semibold text-emerald-700">{Math.round((f.resp_mktg_hours || 0) * 10) / 10}h</span>
                      </td>
                      <td className="py-3 px-3 text-center">
                        <span className="font-bold text-slate-900">{Math.round((f.total_hours || 0) * 10) / 10}h</span>
                      </td>
                      <td className="py-3 px-3 text-center">
                        {(f.leaves_count || 0) > 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-amber-100 text-amber-700">{f.leaves_count}</span>
                        ) : (
                          <span className="text-xs text-slate-400">0</span>
                        )}
                      </td>
                      <td className="py-3 px-3 text-center text-slate-500 text-xs">{f.latest_week || '—'}</td>
                      <td className="py-3 px-3 text-center"><StatusBadge status={f.latest_status || 'Draft'} /></td>
                      <td className="py-3 px-3 text-center">
                        {f.overdue_count > 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-bold bg-red-100 text-red-700 border border-red-200">
                            <AlertTriangle className="w-3 h-3" /> {f.overdue_count}
                          </span>
                        ) : (
                          <span className="text-xs text-emerald-600 font-medium">None</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredComparison.some(f => f.overdue_count > 0) && (
              <div className="mt-4 border-t border-slate-200 pt-4">
                <p className="text-xs font-bold uppercase tracking-wider text-red-500 mb-3 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> Overdue Items Detail
                </p>
                <div className="space-y-2">
                  {filteredComparison.filter(f => f.overdue_count > 0).map(f => (
                    <div key={f.faculty_id} className="bg-red-50 border border-red-100 rounded-lg p-3">
                      <p className="text-sm font-semibold text-red-800 mb-1.5">{f.faculty_name}</p>
                      <div className="space-y-1">
                        {f.overdue_items.slice(0, 5).map((item, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs text-red-700">
                            <span className="bg-red-200 text-red-800 px-1.5 py-0.5 rounded text-[10px] font-semibold">{item.section}</span>
                            <span className="text-red-600">{item.topic || item.group || 'Untitled'}</span>
                            <span className="text-red-400 ml-auto">{item.week}</span>
                          </div>
                        ))}
                        {f.overdue_items.length > 5 && (
                          <p className="text-[10px] text-red-400">+{f.overdue_items.length - 5} more</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Faculty Comparison Table — Admin only */}
      {/* Centre Staff Status Modal (Task 2) */}
      {showStaffStatus && centreStaffStatus && (
        <div
          data-testid="staff-status-modal"
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowStaffStatus(false)}
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {user?.role === 'CIC' ? 'My Team — Update Status' : 'Centre Staff Update Status'}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {centreStaffStatus.from_date} → {centreStaffStatus.to_date} · {user?.role === 'CIC' ? `Faculty assigned to ${user?.name}` : (centreStaffStatus.centre || 'All centres')}
                </p>
              </div>
              <button data-testid="close-staff-status" onClick={() => setShowStaffStatus(false)} className="text-slate-400 hover:text-slate-700 text-2xl leading-none">×</button>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-[10px] uppercase font-bold text-slate-500">Total Staff</p>
                <p className="text-2xl font-black text-slate-900">{centreStaffStatus.total}</p>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3 text-center">
                <p className="text-[10px] uppercase font-bold text-emerald-700">Regularly Updating</p>
                <p className="text-2xl font-black text-emerald-700">{centreStaffStatus.active}</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <p className="text-[10px] uppercase font-bold text-red-700">Not Updating</p>
                <p className="text-2xl font-black text-red-700">{centreStaffStatus.inactive}</p>
              </div>
            </div>
            {centreStaffStatus.by_centre.map((c) => (
              <div key={c.centre} className="mb-5 border border-slate-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-slate-800">{c.centre}</h4>
                  <span className="text-xs text-slate-500">{c.active}/{c.total} updating</span>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-[11px] font-bold uppercase text-emerald-700 mb-1.5">Updating ({c.active})</p>
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {c.active_users.length > 0 ? c.active_users.map(u => (
                        <div key={u.id} className="text-xs flex justify-between border-b border-slate-100 py-1">
                          <span className="font-medium text-slate-700">{u.name}</span>
                          <span className="text-slate-400">{u.role}</span>
                        </div>
                      )) : <p className="text-xs text-slate-400 italic">None</p>}
                    </div>
                  </div>
                  <div>
                    <p className="text-[11px] font-bold uppercase text-red-700 mb-1.5">Not Updating ({c.inactive})</p>
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {c.inactive_users.length > 0 ? c.inactive_users.map(u => (
                        <div key={u.id} className="text-xs flex justify-between border-b border-slate-100 py-1">
                          <span className="font-medium text-slate-700">{u.name}</span>
                          <span className="text-slate-400">{u.role}</span>
                        </div>
                      )) : <p className="text-xs text-slate-400 italic">All active 🎉</p>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
